import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Building2, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useInvestors } from '@/hooks/useInvestors';
import { formatSar } from '@/lib/trip-math';
import type { InvestorRateInput } from '@/lib/types';

interface InvestorRatesEditorProps {
  rates: InvestorRateInput[];
  onChange: (rates: InvestorRateInput[]) => void;
  clientRateAtTarget: number;
  driverRateAtTarget: number;
  /** Optional: lock investor selection for these IDs (already added) */
  disabled?: boolean;
}

/**
 * Manages the list of (investor, rate) pairs for a contract.
 *
 * Constraints:
 *   - Investor rate cannot exceed client_rate (would mean negative company margin)
 *   - Each investor can only appear once
 *   - Driver rate is shown for context (driver gets paid out of investor's gross)
 *
 * Supports adding company-as-investor records (BASMA, CDC, JASR self-investors)
 * with full client_rate so company-owned vehicles work seamlessly.
 */
export function InvestorRatesEditor({
  rates,
  onChange,
  clientRateAtTarget,
  driverRateAtTarget,
  disabled,
}: InvestorRatesEditorProps) {
  const { t } = useTranslation();
  const { data: investorsData } = useInvestors({ pageSize: 100, status: 'active' });
  const investors = investorsData?.items ?? [];

  const [showAdd, setShowAdd] = useState(false);
  const [newInvestorId, setNewInvestorId] = useState('');
  const [newRate, setNewRate] = useState<number>(0);

  const usedIds = new Set(rates.map((r) => r.investorId));
  const availableInvestors = investors.filter((i) => !usedIds.has(i.id));

  const handleAdd = () => {
    if (!newInvestorId || newRate <= 0) return;
    if (newRate > clientRateAtTarget) return;
    onChange([
      ...rates,
      {
        investorId: newInvestorId,
        investorRateAtTarget: newRate,
      },
    ]);
    setNewInvestorId('');
    setNewRate(0);
    setShowAdd(false);
  };

  const handleRemove = (idx: number) => {
    onChange(rates.filter((_, i) => i !== idx));
  };

  const handleUpdateRate = (idx: number, rate: number) => {
    const next = [...rates];
    next[idx] = { ...next[idx], investorRateAtTarget: rate };
    onChange(next);
  };

  // Quick-fill: company-as-investor at full client rate
  const handleQuickFillCompanySelf = (investorId: string) => {
    onChange([
      ...rates,
      { investorId, investorRateAtTarget: clientRateAtTarget },
    ]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-2">
        <div>
          <Label>{t('contracts.investorRates.title')}</Label>
          <p className="text-[11px] text-muted-foreground">
            {t('contracts.investorRates.subtitle')}
          </p>
        </div>
        {availableInvestors.length > 0 && !disabled && (
          <Button type="button" variant="outline" size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-3 w-3" />
            {t('contracts.investorRates.add')}
          </Button>
        )}
      </div>

      {/* Existing rates */}
      {rates.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center">
          <p className="text-sm text-muted-foreground">{t('contracts.investorRates.empty')}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.investorRates.emptyHelp')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rates.map((r, idx) => {
            const inv = investors.find((i) => i.id === r.investorId);
            if (!inv) return null;
            const exceeds = r.investorRateAtTarget > clientRateAtTarget;
            const companyMargin = clientRateAtTarget - r.investorRateAtTarget;

            return (
              <div key={`${r.investorId}-${idx}`} className={`rounded-md border p-3 ${
                exceeds ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/10'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 ${inv.isCompanySelf ? 'text-amber-500' : 'text-blue-500'}`}>
                    {inv.isCompanySelf ? <Building2 className="h-5 w-5" /> : <User className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {inv.fullNameEn}
                          {inv.isCompanySelf && (
                            <span className="ms-2 text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400">
                              {t('contracts.investorRates.companySelf')}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">{inv.code}</p>
                      </div>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => handleRemove(idx)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          aria-label={t('common.remove')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number" step="0.01" min={0} dir="ltr"
                          value={r.investorRateAtTarget}
                          onChange={(e) => handleUpdateRate(idx, Number(e.target.value) || 0)}
                          className="h-8 w-24 font-mono text-sm"
                          disabled={disabled}
                        />
                        <span className="text-xs text-muted-foreground">{t('common.sar')}</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        / {t('contracts.preview.target')}
                      </span>
                      <div className="ms-auto text-[11px]">
                        <span className="text-muted-foreground">{t('contracts.investorRates.companyMargin')}:</span>
                        <span className={`ms-1 font-mono font-medium ${companyMargin >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                          {formatSar(companyMargin, { showCurrency: false })}
                        </span>
                      </div>
                    </div>

                    {exceeds && (
                      <p className="text-[11px] text-destructive flex items-start gap-1 mt-2">
                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                        {t('contracts.investorRates.exceedsClientRate')}
                      </p>
                    )}
                    {driverRateAtTarget > r.investorRateAtTarget && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1 mt-2">
                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                        {t('contracts.investorRates.driverExceedsInvestor', {
                          driver: formatSar(driverRateAtTarget, { showCurrency: false }),
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add new */}
      {showAdd && (
        <div className="rounded-md border border-gold/40 bg-gold/5 p-3 space-y-3">
          <div>
            <Label>{t('contracts.investorRates.investor')}</Label>
            <select
              value={newInvestorId}
              onChange={(e) => setNewInvestorId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1"
            >
              <option value="">{t('contracts.investorRates.selectInvestor')}</option>
              {availableInvestors
                .sort((a, b) => Number(b.isCompanySelf) - Number(a.isCompanySelf))
                .map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.isCompanySelf ? '🏢 ' : ''}{inv.code} — {inv.fullNameEn}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <Label>{t('contracts.investorRates.rate')}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" step="0.01" min={0} max={clientRateAtTarget} dir="ltr"
                value={newRate || ''}
                onChange={(e) => setNewRate(Number(e.target.value) || 0)}
                placeholder={`Max: ${clientRateAtTarget}`}
                className="font-mono"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {t('common.sar')} / {t('contracts.preview.target')}
              </span>
            </div>
            {newRate > clientRateAtTarget && (
              <p className="text-[11px] text-destructive mt-1">
                {t('contracts.investorRates.exceedsClientRate')}
              </p>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => { setShowAdd(false); setNewInvestorId(''); setNewRate(0); }}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="gold"
              size="sm"
              onClick={handleAdd}
              disabled={!newInvestorId || newRate <= 0 || newRate > clientRateAtTarget}
            >
              {t('contracts.investorRates.confirmAdd')}
            </Button>
          </div>
        </div>
      )}

      {/* Quick-fill suggestions for company-as-investor records */}
      {!showAdd && rates.length === 0 && availableInvestors.some((i) => i.isCompanySelf) && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
          <p className="text-[11px] text-muted-foreground mb-2">
            {t('contracts.investorRates.quickFillHint')}
          </p>
          <div className="flex flex-wrap gap-2">
            {availableInvestors
              .filter((i) => i.isCompanySelf)
              .map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => handleQuickFillCompanySelf(inv.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
                >
                  <Building2 className="h-3 w-3" />
                  {inv.fullNameEn.replace(' (Company-Self)', '')}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
